import { Input, useNumberMask } from '@web/components/atoms/Input'
import { Box, Center, Flex } from '@web/components/atoms/Layout'
import { Body, Caption } from '@web/components/atoms/Typography'
import { useBrandingsStore } from '@web/components/utils/branding/useBrandingsStore'
import useClickOutside from '@web/components/utils/useClickOutside'
import { useEffect, useRef, useState } from 'react'
import { useDonationFormContext } from '../../DonationFormProvider'
import PageRadioButton, { PageRadioButtonStyles } from '../atoms/PageRadioButton'
import { toDisplayAmountNoDecimal } from '@web/components/utils/format'
import { DONATION_FREQUENCY_TO_LABEL, useGivingFrequency } from '../../donationUtils'
import { DonationFrequency } from '@web/graphql-components'

type OtherDonateButtonProps = {
  updateAmount: (amount: number | null) => void
  onClick: () => void
  isActive: boolean
  styles?: PageRadioButtonStyles
}

const OtherDonateButton = ({ updateAmount, styles, onClick, isActive }: OtherDonateButtonProps) => {
  const [isEditingAmount, setIsEditingAmount] = useState(false)
  const amount = useDonationFormContext(({ formValues }) => formValues.amount)
  const givingFrequency = useGivingFrequency()
  const { settings, errors, actions } = useDonationFormContext(({ settings, errors, actions }) => ({
    settings,
    errors,
    actions,
  }))
  const [hasMounted, setHasMounted] = useState(false)
  const { dollarHandles, minimumAmount } = settings
  const { updateErrors } = actions
  const { colorData } = useBrandingsStore(({ colorData, buttonData }) => ({ colorData, buttonData }))
  const inputRef = useRef(null)
  useClickOutside(inputRef, () => setIsEditingAmount(false))
  const [lastCustomAmountMask, lastCustomAmount, setLastCustomAmount] = useNumberMask(useState<number | null>(null))

  useEffect(() => {
    if (isEditingAmount) {
      updateAmount(lastCustomAmount)

      if (lastCustomAmount && minimumAmount && lastCustomAmount < minimumAmount) {
        updateErrors({ amount: `Amount must be at least $${settings.minimumAmount}` })
      }
    }

    const isLowerThanMinimum = minimumAmount && amount && amount < minimumAmount
    if (isEditingAmount || !isActive || (amount !== null && amount > 0 && !isLowerThanMinimum)) {
      return
    }

    updateErrors({
      amount: isLowerThanMinimum ? `Amount must be at least $${settings.minimumAmount}` : 'Please enter an amount',
    })
  }, [isEditingAmount])

  useEffect(() => {
    if (isActive && amount !== lastCustomAmount) {
      setLastCustomAmount(amount?.toString() || '')
    }
  }, [amount])

  useEffect(() => {
    if (!hasMounted) {
      setHasMounted(true)
      return
    }

    updateAmount(lastCustomAmount)

    if (lastCustomAmount && minimumAmount && lastCustomAmount < minimumAmount) {
      updateErrors({ amount: `Amount must be at least $${settings.minimumAmount}` })
    }
  }, [lastCustomAmount])

  return isEditingAmount ? (
    <Box w="100%">
      <Box
        ref={inputRef}
        borderRadius={6}
        css={{
          border: '1px solid',
          borderColor: errors.amount ? 'ge.text.danger' : (styles?.selectedColor ?? colorData['Brand']),
          bgColor: 'ge.white',
        }}
        w="100%"
      >
        <Center>
          <Flex px="16px" h="50px" alignItems="center" justifyContent="center" gap="4px" color="ge.text.primary">
            <Body fontWeight={500} fontSize="20px" w="100%" textAlign="right">
              $
            </Body>
            <Input
              fontWeight={500}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setIsEditingAmount(false)
                }
              }}
              fontSize="20px"
              variant="subtle"
              onBlur={() => setIsEditingAmount(false)}
              value={lastCustomAmountMask || ''}
              autoFocus={true}
              inputMode="decimal"
              onWheel={(e) => (e.target as HTMLElement).blur()}
              border="none"
              outline="none"
              onChange={(e) => setLastCustomAmount(e.target.value)}
              bgColor="transparent"
              w="100%"
            />
          </Flex>
        </Center>
      </Box>
      {errors.amount && <Caption color="ge.text.danger" fontSize="0.875rem">{`${errors.amount}`}</Caption>}
    </Box>
  ) : (
    <Box w="100%">
      <PageRadioButton
        id="dollar-handle-custom"
        buttonText={
          isActive && amount
            ? `$ ${toDisplayAmountNoDecimal(amount)}`
            : `$ Enter ${dollarHandles.length > 0 ? 'Custom ' : ''}${givingFrequency !== DonationFrequency.OneTime ? `${DONATION_FREQUENCY_TO_LABEL[givingFrequency]} ` : ''}Amount`
        }
        onClick={() => {
          onClick()
          setIsEditingAmount(true)
        }}
        isError={Boolean(errors.amount)}
        isActive={isActive}
        styles={{ ...styles, width: '100%' }}
        css={{
          '@container page (max-width: 599px)': {
            whiteSpace: 'normal',
            fontSize:
              (amount && amount > 0) || givingFrequency === DonationFrequency.OneTime ? '20px' : '16px !important',
          },
        }}
      />
      {errors.amount && <Caption color="ge.text.danger" fontSize="0.875rem">{`${errors.amount}`}</Caption>}
    </Box>
  )
}

export default OtherDonateButton
